import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import SubPage, { PageAction, PageActions } from '#/common/components/SubPage';
import useWorkOrderQuery from '../hooks/useWorkOrderQuery';
import useNotFoundGuard from '#/common/hooks/useNotFoundGuard';
import RecordNotFound from '#/app/pages/errors/RecordNotFound';
import WorkOrderForm from '../components/WorkOrderForm';
import FsmWorkOrderPreview from '../components/FsmWorkOrderPreview';
import getFormTitle from '#/common/utils/getFormTitle';
import { Tab } from '#/common/components/SubPage/SubPageProps';
import Expenses from '#/modules/expense/components/Expenses';
import WorkOrders from '../components/WorkOrders';
import { fsmLineItemsVisibleColumns } from '../components/WorkOrderDataGrid/fsmColumns';
import type { Filter } from '#/common/components/DataGrid';
import If from '#/common/components/If';
import fields from '../components/WorkOrderForm/fields';
import useWorkOrderUpdate from '../hooks/useWorkOrderUpdate';
import { SubmitWorkOrder } from '../utils/types';
import { WorkOrderDetails } from '#/graphql/types';
import useWorkOrderDelete from '../hooks/useWorkOrderDelete';
import useWorkOrderRelatedCounts from '../hooks/useWorkOrderRelatedCountsQuery';
import useWorkOrderSeeAsUser from '../hooks/useWorkOrderSeeAsUser';

export interface WorkOrderParams {
  readonly id?: string;
}
export type WorkOrderTabs = 'details' | 'expenses' | 'line-items';

export const defaultTab: WorkOrderTabs = 'details';

export interface WorkOrderProps {
  readonly tab?: WorkOrderTabs;
}

const tabs: Tab<WorkOrderTabs>[] = [
  {
    key: 'details',
    text: 'Details',
    type: 'form',
  },
  {
    key: 'expenses',
    text: 'Expenses',
    type: 'grid',
  },
];

const fsmTabs: Tab<WorkOrderTabs>[] = [
  {
    key: 'details',
    text: 'Details',
    type: 'form',
  },
  {
    key: 'line-items',
    text: 'Line Items',
    type: 'grid',
  },
];

const expensesOmitCols = ['workOrder.title'];

const WorkOrderPage: React.FC<WorkOrderProps> = ({ tab }) => {
  const { id } = useParams() as WorkOrderParams;
  const workOrderQuery = useWorkOrderQuery(id);
  const workOrder = workOrderQuery.data?.getWorkOrder?.data;
  const isNotFound = useNotFoundGuard({ loading: workOrderQuery.loading, id, entity: workOrder });

  // Only records synced via the FSM bulk-import source render as FSM (read-only preview).
  // Other third-party sources (e.g. CSV import) render as regular, editable Work Orders.
  const isFsm = workOrder?.thirdPartySource === 'FSM';

  const isLineItem = isFsm && !workOrder?.isParent && workOrder?.parentId;

  const { data: workOrderRelatedCounts } = useWorkOrderRelatedCounts(id ?? '');
  const lineItemsCount = workOrderRelatedCounts?.children ?? 0;

  const { seeWorkOrderAsUser } = useWorkOrderSeeAsUser();

  useEffect(() => {
    if (id) seeWorkOrderAsUser(id);
  }, [id, seeWorkOrderAsUser]);

  const workOrderTransformed = useMemo(() => {
    const res = { ...workOrder } as WorkOrderDetails;

    try {
      if (workOrder?.duration) {
        const [hours = '0', minutes = '0', seconds = '0'] = workOrder.duration.split(':');
        const totalHours = Number(hours) + Number(minutes) / 60 + Number(seconds) / 3600;

        res.duration = Number.isFinite(totalHours) ? totalHours.toFixed(2) : null;
      } else {
        res.duration = null;
      }
    } catch (e) {
      console.error('Error transforming duration for work order', e);
      res.duration = null;
    }

    return res;
  }, [workOrder]);

  const tenantId = workOrderTransformed?.tenantId;

  const title = isFsm
    ? getFormTitle(id, workOrderTransformed?.thirdPartyNumber, 'FSM Work Order')
    : getFormTitle(id, workOrderTransformed?.displayNumber, 'Work Order');

  const fsmWorkOrderTabs = useMemo(
    () =>
      fsmTabs.map((t) => {
        if (t.key === 'line-items')
          return {
            ...t,
            count: lineItemsCount,
          };
        return t;
      }),
    [lineItemsCount],
  );

  const workOrderTabs = useMemo(
    () =>
      tabs.map((t) => {
        if (t.key === 'expenses')
          return {
            ...t,
            count: workOrderRelatedCounts?.expenses ?? 0,
          };
        return t;
      }),
    [workOrderRelatedCounts, id, tenantId],
  );

  const currentTabs = useMemo(() => {
    if (workOrderQuery.loading || isLineItem) {
      return [];
    }
    return isFsm ? fsmWorkOrderTabs : workOrderTabs;
  }, [fsmWorkOrderTabs, workOrderQuery.loading, workOrderTabs, isFsm, isLineItem]);

  const { updateWorkOrder } = useWorkOrderUpdate();
  const submit = useCallback<SubmitWorkOrder>(
    async (workOrder: WorkOrderDetails) => {
      if (!id) {
        return undefined;
      }

      const { data, errors } = (await updateWorkOrder(id, workOrder)) as {
        data?: WorkOrderDetails;
        errors?: string[];
      };

      seeWorkOrderAsUser(id);

      return {
        data: data ?? undefined,
        errors: errors ?? undefined,
      };
    },
    [id, updateWorkOrder, seeWorkOrderAsUser],
  );

  // Delete Operation
  const { deleteWorkOrder: delWorkOrder } = useWorkOrderDelete();
  const deleteWorkOrder = useCallback(() => {
    if (!id) {
      return undefined;
    }

    return delWorkOrder(id);
  }, [delWorkOrder, id]);

  const actions = useMemo<PageAction[]>(() => {
    const expenseWoId = workOrder?.id ? `?workOrderId=${workOrder.id}` : '';

    return [
      {
        id: `new-expense`,
        alwaysShow: true,
        type: 'button',
        props: {
          visible: tab === 'expenses' && !isFsm && workOrderTransformed?.state !== 'closed',
          type: 'submit',
          icon: 'add',
          label: `Create New Expense`,
          responsive: true,
          path: `/expenses/new${expenseWoId}`,
        },
      },
    ];
  }, [isFsm, tab, workOrder?.id, workOrderTransformed?.state]);

  const fsmLineItemsFilters: Filter[] = useMemo(
    () => [
      ['parentId', '=', id],
      'and',
      [
        ['thirdPartyId', '<>', null],
        'or',
        ['thirdPartySource', '<>', null],
        'or',
        ['thirdPartyNumber', '<>', null],
      ],
    ],
    [id],
  );

  if (isNotFound) {
    return (
      <SubPage title={title} showGoBackButton type="form" isLoading={false}>
        <RecordNotFound backUrl="/work-orders" />
      </SubPage>
    );
  }

  return (
    <SubPage
      type={!tab || tab === 'details' ? 'form' : 'list'}
      selectedTab={tab || 'details'}
      tabs={currentTabs}
      title={title}
      isLoading={workOrderQuery.loading}
      showGoBackButton>
      <PageActions actions={actions} />
      <If condition={tab === 'details' || tab === undefined}>
        {workOrderTransformed && isFsm && (
          <FsmWorkOrderPreview
            key={`fsm-wo-preview-${workOrderTransformed.id}-${workOrderTransformed.updatedAt}`}
            workOrder={workOrderTransformed}
            lineItemsCount={lineItemsCount}
          />
        )}
        {workOrderTransformed && !isFsm && (
          <WorkOrderForm
            key={`work-order-form-${workOrderTransformed.id}-${workOrderTransformed.updatedAt}`}
            title={title}
            workOrder={workOrderTransformed}
            fields={fields}
            deleteWorkOrder={deleteWorkOrder}
            submit={submit}
          />
        )}
      </If>
      <If condition={tab === 'line-items' && isFsm}>
        <WorkOrders
          hideNewAction
          hideBulkActions
          permanentFilters={fsmLineItemsFilters}
          visibleColumns={fsmLineItemsVisibleColumns}
          gridId="fsm-wo-line-items"
        />
      </If>
      <If condition={tab === 'expenses' && !isFsm}>
        <Expenses
          omitColumns={expensesOmitCols}
          permanentFilters={id ? [['workOrderId', '=', id]] : undefined}
        />
      </If>
    </SubPage>
  );
};

export default React.memo(WorkOrderPage);
